import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Search from "@mui/icons-material/Search";
import { AutocompleteRenderOptionState, createFilterOptions } from "@mui/material/Autocomplete";
import { FilterOptionsState } from "@mui/material/useAutocomplete";
import FilterOptionMenu from "csi/comments/FilterOptionMenu";
import IconButton from "designSystem/components/button/IconButton";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import { useLocale } from "designSystem/locale";
import Autocomplete from "designSystem/components/select/AutoCompleteList";
import Select from "designSystem/components/select/Select";
import OverflowText from "designSystem/components/text/OverflowText";
import { ResultItem } from "csi/state/comments";
import { useStore } from "csi/state/hooks";
import UserAvatar from "csi/user/UserAvatar";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, ReactNode, useState } from "react";
import { SyntheticEvent } from "react";
import { ComponentItem } from "designSystem/types/component-item-types";

const SearchFilterHeader = observer(() => {
	const loc = useLocale();
	const { commentsStore } = useStore();
	const {
		filterByItem,
		filterCategory,
		filterCategoryItems,
		searchFocused,
		enterSearchMode,
		leaveSearchMode,
		setSearchText,
		optionsForSearch,
		searchText,
		selectCommentByUrl,
	} = commentsStore;

	const [inputValue, setInputValue] = useState(searchText);
	const [popupOpen, setPopupOpen] = useState(false);

	const filter = createFilterOptions<ResultItem>();

	const searchEntry = {
		isComment: false,
		isSearchEntry: true,
		searchText: "",
	};

	const switchToSearchBar = () => {
		// when click the search icon, switch to the search mode */
		enterSearchMode();
	};

	const reset = () => {
		// handle the logic when the user click the close icon */
		setTimeout(() => leaveSearchMode(), 100);
		setInputValue("");
		setPopupOpen(false);
	};

	const renderSelectedItem = (value: ComponentItem): ReactNode => {
		// customize the option
		const item = filterCategoryItems.find(({ id }) => id === value.id);
		return (
			<FlexRowCenter>
				<OverflowText text={item.name} />
			</FlexRowCenter>
		);
	};

	const filterOptions = (options: ResultItem[], params: FilterOptionsState<ResultItem>) => {
		// show search entry with search icon in dropdown only if user type some text to search
		if (inputValue) {
			options[0].searchText = inputValue;
		} else {
			options.shift();
		}

		const filtered = filter(options, params) as ResultItem[];

		if (inputValue !== "" && filtered.length == 1 && filtered[0].isSearchEntry) {
			return [];
		}
		return filtered;
	};

	const onBlur = () => {
		setPopupOpen(false);
		setInputValue(searchText);
	};

	/** Value is any in order to bypass Mui expecting that potentially onChange has an array value */
	const onChange = (event: SyntheticEvent<Element, Event>, value: any) => {
		// handle the logic when the user select one item;
		// onChange is called when search string is empty (for some reason), so do nothing in that case
		if (value) {
			const { isSearchEntry, searchText, authorName, isComment, url } = value;

			if (isSearchEntry) {
				setSearchText(searchText, false);
			} else if (isComment) {
				selectCommentByUrl(url);
			} else {
				setSearchText(authorName, true);
			}
		}
	};

	const onInputChange = (event: React.ChangeEvent<{}>, newInputValue: string) => {
		if (event) {
			setInputValue(newInputValue);

			if (!popupOpen) {
				// when start typing, open up the dropdown
				setPopupOpen(true);
			}
		} else {
			// this would happen when go back from a main comment, maintain the previous search text
			setPopupOpen(false);
			setInputValue(searchText);
		}
	};

	const getOptionLabel = ({ isSearchEntry, searchText, isComment, subject, authorName }): string => {
		if (isSearchEntry) {
			return searchText;
		} else if (isComment) {
			return subject;
		} else {
			return authorName;
		}
	};

	function renderSearchList(
		props: React.HTMLAttributes<HTMLLIElement>,
		{
			isSearchEntry,
			searchText,
			authorName,
			author,
			subject,
			isComment,
		}: ResultItem,
	) {
		let text = authorName;
		let component = <UserAvatar user={author} />;
		if (isSearchEntry) {
			text = searchText;
			component = <Search color="action" />;
		}

		return (
			<li {...props} key={props.id}>
				<FlexRowCenter>
					<Box mr={2}>
						{component}
					</Box>
					<Box flexGrow={1}>
						<OverflowText text={text} />
						{isComment && <OverflowText bold text={subject} />}
					</Box>
				</FlexRowCenter>
			</li>
		);
	}

	return (
		<FlexRowCenter>
			<Box flexGrow={1}>
				{searchFocused ? (
					<FlexRowCenter>
						<Box flexGrow={1} mr={1}>
							<Autocomplete<ResultItem>
								autoHighlight
								filterOptions={filterOptions}
								getOptionLabel={getOptionLabel}
								inputValue={inputValue}
								isOptionEqualToValue={(option, value) => option.url === value.url}
								onBlur={onBlur}
								onChange={onChange}
								onInputChange={onInputChange}
								open={popupOpen}
								options={[searchEntry, ...optionsForSearch]}
								renderOption={renderSearchList}
								searchTextProps={{
									hideClearButton: true,
									placeholder: loc.resource("CommentSearchPlaceHolder"),
									onFocus: enterSearchMode,
									onEscape: reset,
								}}
							/>
						</Box>
						<IconButton
							aria-label="close"
							onClick={reset}
							size="large"
							style={{ marginLeft: 4 }}
						>
							<CloseIcon />
						</IconButton>
					</FlexRowCenter>
				)
					: (
						<Select
							fullWidth
							items={filterCategoryItems}
							onSelectItem={filterByItem}
							renderSelectedItemFunc={(value) => renderSelectedItem(value)}
							value={filterCategory}
						/>
					)}
			</Box>
			{!searchFocused && (
				<IconButton
					onClick={switchToSearchBar}
					size="large"
					style={{ marginLeft: 8 }}
				>
					<Search />
				</IconButton>
			)}
			<FilterOptionMenu
				style={{ marginLeft: 4 }}
			/>
		</FlexRowCenter>
	);
});

export default SearchFilterHeader;